/**
 * Pagina Consumi AI — Fasi 7 e 8 billing.
 *
 * Mostra totali, ripartizioni, log dettagliato, esportazione CSV
 * e (con ai_usage.manage) cancellazione manuale per intervallo.
 * Protetta da permesso ai_usage.view (via PermissionRoute in App.tsx).
 */

import { useEffect, useState } from 'react'
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
  theme,
} from 'antd'
import {
  DeleteOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs, { type Dayjs } from 'dayjs'
import axiosInstance from '@/lib/axios'
import { usePageTitle } from '@/contexts/PageTitleContext'
import { usePermission } from '@/hooks/usePermission'

const { RangePicker } = DatePicker
const { Text } = Typography
const BASE = '/ai-usage'

// ── Tipi ──────────────────────────────────────────────────────────────────────

interface Ripartizione {
  chiave: string
  n_chiamate: number
  totale_input_tokens: number
  totale_output_tokens: number
  costo_eur: number
}

interface Riepilogo {
  n_chiamate: number
  totale_input_tokens: number
  totale_output_tokens: number
  totale_cache_creation_tokens: number
  totale_cache_read_tokens: number
  totale_costo_eur: number
  per_provider: Ripartizione[]
  per_ambiente: Ripartizione[]
  per_funzione: Ripartizione[]
  per_modello: Ripartizione[]
}

interface RigaLog {
  id: number
  created_at: string | null
  ambiente: string
  provider: string
  funzione: string
  modello: string
  unita: string
  input_tokens: number | null
  output_tokens: number | null
  cache_creation_tokens: number | null
  cache_read_tokens: number | null
  costo_decimillesimi_eur: number | null
  tasso_cambio: number | null
  user_id: number | null
  entita_tipo: string | null
  entita_id: number | null
  esito: string
}

interface ElencoResp {
  total: number
  page: number
  size: number
  items: RigaLog[]
}

interface AnteprimaCanc {
  n_righe: number
  costo_eur: number
  da: string
  a: string
}

// ── Costanti UI ───────────────────────────────────────────────────────────────

const ESITO_COLOR: Record<string, string> = {
  ok: 'success',
  errore: 'error',
  fallback: 'warning',
  in_attesa: 'default',
}

const FUNZIONE_LABEL: Record<string, string> = {
  ocr_libretto:          'OCR libretto',
  ocr_patente:           'OCR patente',
  ocr_documento_cliente: 'OCR documento',
  bi_sql:                'BI / SQL',
  procurement_expand:    'Approv. espansione',
  procurement_rank:      'Approv. ranking',
  tire_service:          'Servizio gomme',
  dettatura:             'Dettatura',
}

const fmtN   = (n: number) => n.toLocaleString('it-IT')
const fmtEur = (n: number) =>
  n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })

// ── Colonne tabelle ───────────────────────────────────────────────────────────

const ripartizioneCols: ColumnsType<Ripartizione> = [
  {
    title: 'Chiave',
    dataIndex: 'chiave',
    render: (v: string) => FUNZIONE_LABEL[v] ?? v,
  },
  {
    title: 'Chiamate',
    dataIndex: 'n_chiamate',
    align: 'right',
    render: fmtN,
  },
  {
    title: 'Token in',
    dataIndex: 'totale_input_tokens',
    align: 'right',
    render: fmtN,
  },
  {
    title: 'Token out',
    dataIndex: 'totale_output_tokens',
    align: 'right',
    render: fmtN,
  },
  {
    title: 'Costo',
    dataIndex: 'costo_eur',
    align: 'right',
    render: (v: number) => <Text strong>{fmtEur(v)}</Text>,
  },
]

const elencoCols: ColumnsType<RigaLog> = [
  {
    title: 'ID',
    dataIndex: 'id',
    width: 60,
  },
  {
    title: 'Data',
    dataIndex: 'created_at',
    width: 145,
    render: (v: string | null) => v ? dayjs(v).format('DD/MM/YY HH:mm') : '—',
  },
  {
    title: 'Funzione',
    dataIndex: 'funzione',
    render: (v: string) => FUNZIONE_LABEL[v] ?? v,
  },
  {
    title: 'Modello',
    dataIndex: 'modello',
    ellipsis: true,
    render: (v: string) => <Text code style={{ fontSize: 11 }}>{v}</Text>,
  },
  {
    title: 'Inp',
    dataIndex: 'input_tokens',
    align: 'right',
    width: 70,
    render: (v: number | null) => v != null ? fmtN(v) : '—',
  },
  {
    title: 'Out',
    dataIndex: 'output_tokens',
    align: 'right',
    width: 70,
    render: (v: number | null) => v != null ? fmtN(v) : '—',
  },
  {
    title: 'Costo (€)',
    dataIndex: 'costo_decimillesimi_eur',
    align: 'right',
    width: 100,
    render: (v: number | null) =>
      v != null ? fmtEur(v / 10000) : <Text type="secondary">—</Text>,
  },
  {
    title: 'Amb.',
    dataIndex: 'ambiente',
    width: 90,
    render: (v: string) => (
      <Tag color={v === 'produzione' ? 'blue' : 'default'} style={{ fontSize: 11 }}>
        {v}
      </Tag>
    ),
  },
  {
    title: 'Esito',
    dataIndex: 'esito',
    width: 90,
    render: (v: string) => (
      <Badge
        status={ESITO_COLOR[v] as 'success' | 'error' | 'warning' | 'default'}
        text={v}
      />
    ),
  },
]

// ── Componente principale ──────────────────────────────────────────────────────

export default function ConsumiAiPage() {
  const { setTitle } = usePageTitle()
  const { token } = theme.useToken()
  const qc = useQueryClient()
  const canManage = usePermission('ai_usage.manage')

  useEffect(() => { setTitle('Consumi AI') }, [setTitle])

  // ── Stato filtri elenco ───────────────────────────────────────────────────
  const now = dayjs()
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    now.startOf('month'),
    now.endOf('month'),
  ])
  const [filtroFunzione, setFiltroFunzione] = useState<string | undefined>()
  const [filtroModello,  setFiltroModello]  = useState<string | undefined>()
  const [filtroEsito,    setFiltroEsito]    = useState<string | undefined>()
  const [filtroProvider, setFiltroProvider] = useState<string | undefined>()
  const [filtroAmbiente, setFiltroAmbiente] = useState<string | undefined>()
  const [page, setPage] = useState(1)

  const da = dateRange[0].format('YYYY-MM-DD')
  const a  = dateRange[1].format('YYYY-MM-DD')

  // ── Stato cancellazione ───────────────────────────────────────────────────
  const [cancRange, setCancRange] = useState<[Dayjs, Dayjs] | null>(null)
  const [anteprimaCanc, setAnteprimaCanc] = useState<AnteprimaCanc | null>(null)
  const [cancLoading, setCancLoading] = useState(false)
  const [cancModalOpen, setCancModalOpen] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)

  // ── Queries ───────────────────────────────────────────────────────────────
  const qRiep = useQuery<Riepilogo>({
    queryKey: ['ai-usage-riepilogo', da, a],
    queryFn: async () => {
      const r = await axiosInstance.get<Riepilogo>(`${BASE}/riepilogo`, {
        params: { da, a },
      })
      return r.data
    },
  })

  const qEl = useQuery<ElencoResp>({
    queryKey: [
      'ai-usage-elenco', da, a,
      filtroFunzione, filtroModello, filtroEsito, filtroProvider, filtroAmbiente,
      page,
    ],
    queryFn: async () => {
      const r = await axiosInstance.get<ElencoResp>(`${BASE}/elenco`, {
        params: {
          da, a,
          ...(filtroFunzione && { funzione: filtroFunzione }),
          ...(filtroModello  && { modello:  filtroModello }),
          ...(filtroEsito    && { esito:    filtroEsito }),
          ...(filtroProvider && { provider: filtroProvider }),
          ...(filtroAmbiente && { ambiente: filtroAmbiente }),
          page,
          size: 50,
        },
      })
      return r.data
    },
  })

  // ── Mutazione cancellazione ───────────────────────────────────────────────
  const mutCanc = useMutation({
    mutationFn: async ({ da: d, a: aa }: { da: string; a: string }) => {
      const r = await axiosInstance.delete(`${BASE}`, { params: { da: d, a: aa } })
      return r.data
    },
    onSuccess: () => {
      setCancModalOpen(false)
      setAnteprimaCanc(null)
      setCancRange(null)
      qc.invalidateQueries({ queryKey: ['ai-usage-riepilogo'] })
      qc.invalidateQueries({ queryKey: ['ai-usage-elenco'] })
    },
  })

  // ── Handlers ─────────────────────────────────────────────────────────────
  const resetPage = () => setPage(1)

  const handleExport = async () => {
    setExportLoading(true)
    try {
      const params = new URLSearchParams({ da, a })
      if (filtroFunzione) params.set('funzione', filtroFunzione)
      if (filtroModello)  params.set('modello',  filtroModello)
      if (filtroEsito)    params.set('esito',    filtroEsito)
      if (filtroProvider) params.set('provider', filtroProvider)
      if (filtroAmbiente) params.set('ambiente', filtroAmbiente)
      // Scarica il file via fetch autenticato
      const token_ = localStorage.getItem('garage_access_token')
      const resp = await fetch(`/api/v1${BASE}/esporta?${params}`, {
        headers: token_ ? { Authorization: `Bearer ${token_}` } : {},
      })
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
      const blob = await resp.blob()
      const url = URL.createObjectURL(blob)
      const cd = resp.headers.get('content-disposition') ?? ''
      const match = cd.match(/filename="([^"]+)"/)
      const filename = match ? match[1] : `consumi-ai-${da}_${a}.csv`
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (e) {
      console.error('Export failed', e)
    } finally {
      setExportLoading(false)
    }
  }

  const handleAnteprimaCanc = async () => {
    if (!cancRange) return
    setCancLoading(true)
    try {
      const r = await axiosInstance.get<AnteprimaCanc>(`${BASE}/anteprima-cancellazione`, {
        params: {
          da: cancRange[0].format('YYYY-MM-DD'),
          a:  cancRange[1].format('YYYY-MM-DD'),
        },
      })
      setAnteprimaCanc(r.data)
      setCancModalOpen(true)
    } finally {
      setCancLoading(false)
    }
  }

  const handleEsportaEPoiCanc = async () => {
    if (!anteprimaCanc) return
    // Prima esporta l'intervallo di cancellazione
    const token_ = localStorage.getItem('garage_access_token')
    const params = new URLSearchParams({ da: anteprimaCanc.da, a: anteprimaCanc.a })
    const resp = await fetch(`/api/v1${BASE}/esporta?${params}`, {
      headers: token_ ? { Authorization: `Bearer ${token_}` } : {},
    })
    if (resp.ok) {
      const blob = await resp.blob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `consumi-ai-${anteprimaCanc.da}_${anteprimaCanc.a}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    }
    // Poi procede con la cancellazione
    mutCanc.mutate({ da: anteprimaCanc.da, a: anteprimaCanc.a })
  }

  // ── Layout ────────────────────────────────────────────────────────────────
  const cardStyle = {
    borderRadius: token.borderRadius,
    border: `1px solid ${token.colorBorderSecondary}`,
  }

  const riepilogo = qRiep.data

  return (
    <div style={{ padding: 16, maxWidth: 1400, margin: '0 auto' }}>

      {/* ── Selettore periodo ── */}
      <Card size="small" style={{ ...cardStyle, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Text strong>Periodo:</Text>
          <RangePicker
            value={dateRange}
            onChange={v => {
              if (v?.[0] && v?.[1]) {
                setDateRange([v[0], v[1]])
                resetPage()
              }
            }}
            format="DD/MM/YYYY"
            allowClear={false}
            presets={[
              { label: 'Questo mese',  value: [now.startOf('month'), now.endOf('month')] },
              { label: 'Mese scorso',  value: [now.subtract(1, 'month').startOf('month'), now.subtract(1, 'month').endOf('month')] },
              { label: 'Ultimi 7 gg', value: [now.subtract(6, 'day'), now] },
              { label: 'Ultimi 30 gg',value: [now.subtract(29, 'day'), now] },
              { label: "Quest'anno",  value: [now.startOf('year'), now] },
            ]}
          />
        </div>
      </Card>

      {qRiep.isError && (
        <Alert type="error" message="Errore nel caricamento dei dati" style={{ marginBottom: 16 }} />
      )}

      {/* ── Riquadri totali ── */}
      {qRiep.isLoading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : riepilogo && (
        <>
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={8}>
              <Card size="small" style={cardStyle}>
                <Statistic
                  title="Chiamate AI"
                  value={riepilogo.n_chiamate}
                  valueStyle={{ fontSize: 28, fontWeight: 700 }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8}>
              <Card size="small" style={cardStyle}>
                <Statistic
                  title="Token totali"
                  value={riepilogo.totale_input_tokens + riepilogo.totale_output_tokens}
                  formatter={v => fmtN(v as number)}
                  valueStyle={{ fontSize: 28, fontWeight: 700 }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8}>
              <Card size="small" style={cardStyle}>
                <Statistic
                  title="Costo netto periodo"
                  value={riepilogo.totale_costo_eur}
                  precision={2}
                  prefix="€"
                  valueStyle={{ fontSize: 28, fontWeight: 700, color: token.colorPrimary }}
                />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Arrotondato per eccesso al centesimo
                </Text>
              </Card>
            </Col>
          </Row>

          {/* ── Ripartizioni ── */}
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            {[
              { titolo: 'Per fornitore', dati: riepilogo.per_provider },
              { titolo: 'Per ambiente',  dati: riepilogo.per_ambiente },
              { titolo: 'Per funzione',  dati: riepilogo.per_funzione },
              { titolo: 'Per modello',   dati: riepilogo.per_modello  },
            ].map(({ titolo, dati }) => (
              <Col xs={24} md={12} key={titolo}>
                <Card
                  size="small"
                  title={<Text strong style={{ fontSize: 13 }}>{titolo}</Text>}
                  style={cardStyle}
                >
                  <Table<Ripartizione>
                    dataSource={dati}
                    columns={ripartizioneCols}
                    rowKey="chiave"
                    size="small"
                    pagination={false}
                    locale={{ emptyText: 'Nessun dato' }}
                  />
                </Card>
              </Col>
            ))}
          </Row>
        </>
      )}

      {/* ── Elenco dettagliato ── */}
      <Card
        size="small"
        title={<Text strong>Elenco chiamate</Text>}
        style={{ ...cardStyle, marginBottom: 16 }}
        extra={
          <Space>
            {qEl.data && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {fmtN(qEl.data.total)} righe
              </Text>
            )}
            <Button
              icon={<DownloadOutlined />}
              size="small"
              loading={exportLoading}
              onClick={handleExport}
            >
              Esporta CSV
            </Button>
          </Space>
        }
      >
        {/* Filtri */}
        <Row gutter={[8, 8]} style={{ marginBottom: 12 }}>
          <Col xs={12} sm={6} md={4}>
            <Select
              placeholder="Funzione"
              allowClear
              style={{ width: '100%' }}
              value={filtroFunzione}
              onChange={v => { setFiltroFunzione(v); resetPage() }}
              options={Object.entries(FUNZIONE_LABEL).map(([v, l]) => ({ value: v, label: l }))}
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              placeholder="Esito"
              allowClear
              style={{ width: '100%' }}
              value={filtroEsito}
              onChange={v => { setFiltroEsito(v); resetPage() }}
              options={[
                { value: 'ok',        label: 'OK' },
                { value: 'errore',    label: 'Errore' },
                { value: 'fallback',  label: 'Fallback' },
                { value: 'in_attesa', label: 'In attesa' },
              ]}
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              placeholder="Provider"
              allowClear
              style={{ width: '100%' }}
              value={filtroProvider}
              onChange={v => { setFiltroProvider(v); resetPage() }}
              options={[
                { value: 'anthropic', label: 'Anthropic' },
                { value: 'deepgram',  label: 'Deepgram' },
              ]}
            />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select
              placeholder="Ambiente"
              allowClear
              style={{ width: '100%' }}
              value={filtroAmbiente}
              onChange={v => { setFiltroAmbiente(v); resetPage() }}
              options={[
                { value: 'locale',     label: 'Locale' },
                { value: 'produzione', label: 'Produzione' },
              ]}
            />
          </Col>
          {(filtroFunzione || filtroEsito || filtroProvider || filtroAmbiente) && (
            <Col xs={24} sm={6} md={4} style={{ display: 'flex', alignItems: 'center' }}>
              <Text
                type="secondary"
                style={{ fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => {
                  setFiltroFunzione(undefined)
                  setFiltroModello(undefined)
                  setFiltroEsito(undefined)
                  setFiltroProvider(undefined)
                  setFiltroAmbiente(undefined)
                  resetPage()
                }}
              >
                Azzera filtri
              </Text>
            </Col>
          )}
        </Row>

        <div style={{ overflowX: 'auto' }}>
          <Table<RigaLog>
            dataSource={qEl.data?.items ?? []}
            columns={elencoCols}
            rowKey="id"
            size="small"
            loading={qEl.isLoading}
            pagination={{
              current: page,
              pageSize: 50,
              total: qEl.data?.total ?? 0,
              showSizeChanger: false,
              showTotal: (tot) => `${fmtN(tot)} righe`,
              onChange: (p) => setPage(p),
            }}
            locale={{ emptyText: 'Nessuna riga per il periodo selezionato' }}
          />
        </div>
      </Card>

      {/* ── Sezione cancellazione (solo ai_usage.manage) ── */}
      {canManage && (
        <Card
          size="small"
          title={
            <Space>
              <WarningOutlined style={{ color: token.colorError }} />
              <Text strong style={{ color: token.colorError }}>Cancellazione dati</Text>
            </Space>
          }
          style={{ ...cardStyle, borderColor: token.colorErrorBorder }}
        >
          <Alert
            type="warning"
            showIcon
            message="La cancellazione è irreversibile e distrugge dati di fatturazione."
            description="Prima di procedere, esporta sempre il CSV dell'intervallo. L'azione viene tracciata nei log."
            style={{ marginBottom: 16 }}
          />

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Text>Intervallo da cancellare:</Text>
            <RangePicker
              value={cancRange}
              onChange={v => setCancRange(v?.[0] && v?.[1] ? [v[0], v[1]] : null)}
              format="DD/MM/YYYY"
              allowClear
            />
            <Button
              danger
              icon={<DeleteOutlined />}
              disabled={!cancRange}
              loading={cancLoading}
              onClick={handleAnteprimaCanc}
            >
              Verifica e cancella…
            </Button>
          </div>
        </Card>
      )}

      {/* ── Modal di conferma cancellazione ── */}
      <Modal
        open={cancModalOpen}
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: token.colorError }} />
            Conferma cancellazione
          </Space>
        }
        onCancel={() => setCancModalOpen(false)}
        footer={[
          <Button key="annulla" onClick={() => setCancModalOpen(false)}>
            Annulla
          </Button>,
          <Button
            key="esporta-poi-canc"
            icon={<DownloadOutlined />}
            onClick={handleEsportaEPoiCanc}
            loading={mutCanc.isPending}
          >
            Esporta CSV poi cancella
          </Button>,
          <Button
            key="canc"
            danger
            type="primary"
            icon={<DeleteOutlined />}
            loading={mutCanc.isPending}
            onClick={() => {
              if (anteprimaCanc) {
                mutCanc.mutate({ da: anteprimaCanc.da, a: anteprimaCanc.a })
              }
            }}
          >
            Cancella senza esportare
          </Button>,
        ]}
      >
        {anteprimaCanc && (
          <div>
            <p>
              Verranno cancellate{' '}
              <Text strong>{fmtN(anteprimaCanc.n_righe)} righe</Text> nell'intervallo{' '}
              <Text code>{anteprimaCanc.da}</Text> → <Text code>{anteprimaCanc.a}</Text>.
            </p>
            <p>
              Costo totale che verrà eliminato:{' '}
              <Text strong style={{ color: token.colorError }}>
                {fmtEur(anteprimaCanc.costo_eur)}
              </Text>
            </p>
            <Divider style={{ margin: '12px 0' }} />
            <Alert
              type="warning"
              showIcon
              message="È fortemente consigliato esportare il CSV prima di cancellare."
            />
            {mutCanc.isError && (
              <Alert
                type="error"
                message="Errore durante la cancellazione. Riprova."
                style={{ marginTop: 12 }}
              />
            )}
          </div>
        )}
      </Modal>
    </div>
  )
}
